import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/wp-json/lalay/v1/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email, source: 'landing-newsletter' }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.success) {
        setSubmitError(body?.message || 'Could not subscribe — please try again.');
        return;
      }
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 4000);
    } catch (err) {
      setSubmitError('Network error — please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: 'Pinterest', label: 'Pinterest', url: '#' },
    { icon: 'Instagram', label: 'Instagram', url: '#' },
    { icon: 'Twitter', label: 'Twitter', url: '#' },
    { icon: 'Tiktok', label: 'TikTok', url: '#' }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-primary/10">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-card border border-border rounded-3xl p-8 md:p-12 shadow-2xl text-center"
        >
          <div className="mb-6 inline-block">
            <div className="bg-primary/10 rounded-full p-4">
              <Icon name="Mail" size={48} className="text-primary" />
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Follow the Threads
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Subscribe to get exclusive style tips, new arrivals, and special offers delivered to your inbox
          </p>

          {isSubscribed ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-8"
            >
              <div className="flex items-center justify-center gap-2 text-green-600">
                <Icon name="CheckCircle" size={24} />
                <span className="font-semibold">Successfully subscribed!</span>
              </div>
            </motion.div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto mb-8">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  className="flex-1"
                  required
                  disabled={submitting}
                />
                <Button type="submit" size="lg" className="whitespace-nowrap" disabled={submitting}>
                  {submitting ? 'Subscribing…' : 'Subscribe Now'}
                </Button>
              </form>
              {submitError && (
                <p className="text-sm text-destructive max-w-xl mx-auto mb-4 text-center">{submitError}</p>
              )}
            </>
          )}

          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-muted-foreground">Follow us for daily style inspiration</p>
            <div className="flex items-center gap-4">
              {socialLinks?.map((social) => (
                <motion.a
                  key={social?.label}
                  href={social?.url}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-muted hover:bg-primary hover:text-primary-foreground rounded-full p-3 transition-colors"
                  aria-label={social?.label}
                >
                  <Icon name={social?.icon} size={20} />
                </motion.a>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-6">
            By subscribing, you agree to our Privacy Policy and consent to receive updates
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default NewsletterSignup;